import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ModelMetadataSelectComponent } from '../model-metadata-select/model-metadata-select.component';
import { ModelHomeComponent } from '../model-home/model-home.component';
import { ModelMetadataJsonHomeComponent } from '../model-metadata-json-home/model-metadata-json-home.component';
import { UploadFilesComponent } from '../../dataset/ngsild/upload-files/upload-files.component';
import type { ModelOnboardingResult, ModelOnboardingSelection } from '../model.models';

type ModelOnboardingView = 'metadata-select' | 'manual' | 'json-upload';

const FLOW_QUERY_KEY = 'flow';
const FLOW_MANUAL = 'manual';
const FLOW_JSON_UPLOAD = 'json-upload';

@Component({
  selector: 'lib-model-onboarding-home',
  standalone: true,
  imports: [
    CommonModule,
    ModelMetadataSelectComponent,
    ModelHomeComponent,
    ModelMetadataJsonHomeComponent,
    UploadFilesComponent,
  ],
  templateUrl: './model-onboarding-home.component.html',
  styleUrl: './model-onboarding-home.component.scss',
  host: { class: 'feature-model-onboarding-home-host' },
})
export class ModelOnboardingHomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly publisherRole = input<'org-admin' | 'provider'>('org-admin');
  readonly leafImageSrc = input('/assets-leaf.png');
  readonly catalogueModelsPath = input('/models');
  readonly modelDetailPath = input('/model');

  readonly view = signal<ModelOnboardingView>('metadata-select');
  readonly successModalVisible = signal(false);
  readonly uploadModalVisible = signal(false);
  readonly publishSuccessModalVisible = signal(false);
  readonly modelLastPayload = signal<ModelOnboardingResult | null>(null);
  readonly modelCreatedItemId = signal<string | null>(null);

  readonly submitError = output<string>();
  readonly submitSuccess = output<string>();

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const flow = params.get(FLOW_QUERY_KEY);
        if (flow === FLOW_MANUAL) {
          this.view.set('manual');
        } else if (flow === FLOW_JSON_UPLOAD) {
          this.view.set('json-upload');
        } else {
          this.view.set('metadata-select');
        }
      });
  }

  onMetadataSelection(selection: ModelOnboardingSelection): void {
    if (selection.metadataMode === 'manual') {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { [FLOW_QUERY_KEY]: FLOW_MANUAL, step: 0 },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
      this.view.set('manual');
      return;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [FLOW_QUERY_KEY]: FLOW_JSON_UPLOAD, step: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.view.set('json-upload');
  }

  onWizardBack(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [FLOW_QUERY_KEY]: null, step: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.view.set('metadata-select');
  }

  onModelComplete(result: ModelOnboardingResult): void {
    this.modelLastPayload.set(result);
    this.modelCreatedItemId.set(result.itemId);
    this.successModalVisible.set(true);
  }

  onSubmitError(message: string): void {
    this.submitError.emit(message);
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseModals(): void {
    if (this.publishSuccessModalVisible()) return;
    if (this.uploadModalVisible()) {
      this.dismissUploadModal();
      return;
    }
    if (this.successModalVisible()) {
      this.dismissSuccessModal();
    }
  }

  dismissSuccessModal(): void {
    this.successModalVisible.set(false);
  }

  dismissUploadModal(restoreSuccessModal = true): void {
    this.uploadModalVisible.set(false);
    if (restoreSuccessModal) {
      this.successModalVisible.set(true);
    }
  }

  uploadModalFileFormat(): string {
    return this.modelLastPayload()?.payload.metadata.fileFormat?.trim() ?? '';
  }

  uploadModalModelLabel(): string {
    return this.modelLastPayload()?.payload.basic.title?.trim() ?? '';
  }

  uploadModalDataSourceUrlEnabled(): boolean {
    const permission = this.modelLastPayload()?.payload.basic.permission;
    if (!permission) return true;
    const p = String(permission).trim();
    return p === 'Open' || p.toUpperCase() === 'PUBLIC';
  }

  onUploadNowClick(): void {
    this.dismissSuccessModal();
    this.uploadModalVisible.set(true);
  }

  onUploadModalPrevious(): void {
    this.dismissUploadModal(false);
    this.successModalVisible.set(true);
  }

  onUploadPublishSuccess(): void {
    this.uploadModalVisible.set(false);
    this.successModalVisible.set(false);
    this.publishSuccessModalVisible.set(true);
  }

  publishSuccessTitle(): string {
    return 'Model Submitted for Approval';
  }

  publishSuccessMessage(): string {
    return this.publisherRole() === 'provider'
      ? 'Your model has been submitted and is awaiting Organisation Admin approval for metadata and platform admin approval.'
      : 'Your model request has been submitted and is awaiting platform admin approval.';
  }

  onPublishViewModel(): void {
    this.publishSuccessModalVisible.set(false);
    this.dismissUploadModal();
    this.dismissSuccessModal();
    this.onViewModelClick();
  }

  onPublishCreateNew(): void {
    this.publishSuccessModalVisible.set(false);
    this.dismissUploadModal();
    this.dismissSuccessModal();
    this.onWizardBack();
  }

  onUploadLaterClick(): void {
    this.dismissSuccessModal();
    this.onWizardBack();
  }

  onViewModelClick(): void {
    const itemId =
      this.modelCreatedItemId() ?? this.modelLastPayload()?.itemId ?? null;
    if (!itemId) {
      void this.router.navigate([this.catalogueModelsPath()]);
      this.dismissSuccessModal();
      return;
    }
    void this.router.navigate([this.modelDetailPath(), itemId]);
    this.dismissSuccessModal();
  }
}
